import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface AccountConfigState {
  selectedTabIndex?: number;
  selectedSetting?: string;
  selectedSubTab?: string;
  selectedMainTab?: string;
  formData?: any;
  tableState?: any;
  lastVisited?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class AccountConfigStateService {
  private readonly stateMap = new Map<string, AccountConfigState>();
  private readonly currentState$ = new BehaviorSubject<AccountConfigState>({});

  constructor() {}

  /**
   * Get the state key for a specific account
   */
  private getStateKey(clientCode: string, accountCode: string, versionNumber: number): string {
    return `${clientCode}-${accountCode}-v${versionNumber}`;
  }

  /**
   * Save the current state for an account
   */
  saveState(clientCode: string, accountCode: string, versionNumber: number, state: AccountConfigState): void {
    const key = this.getStateKey(clientCode, accountCode, versionNumber);
    const currentState = this.stateMap.get(key) || {};
    const newState = { 
      ...currentState, 
      ...state, 
      lastVisited: new Date() 
    };
    
    this.stateMap.set(key, newState);
    this.currentState$.next(newState);
  }

  /**
   * Retrieve the saved state for an account
   */
  getState(clientCode: string, accountCode: string, versionNumber: number): AccountConfigState | null {
    const key = this.getStateKey(clientCode, accountCode, versionNumber);
    return this.stateMap.get(key) || null;
  }

  /**
   * Clear state for a specific account
   */
  clearState(clientCode: string, accountCode: string, versionNumber: number): void {
    const key = this.getStateKey(clientCode, accountCode, versionNumber);
    this.stateMap.delete(key);
  }

  /**
   * Clear all states (useful for logout)
   */
  clearAllStates(): void {
    this.stateMap.clear();
    this.currentState$.next({});
  }

  /**
   * Get current state observable
   */
  getCurrentState() {
    return this.currentState$.asObservable();
  }

  /**
   * Update specific state properties without replacing the entire state
   */
  updateState(clientCode: string, accountCode: string, versionNumber: number, partialState: Partial<AccountConfigState>): void {
    const key = this.getStateKey(clientCode, accountCode, versionNumber);
    const currentState = this.stateMap.get(key) || {};
    const newState = { 
      ...currentState, 
      ...partialState, 
      lastVisited: new Date() 
    };
    
    this.stateMap.set(key, newState);
    this.currentState$.next(newState);
  }
}